import React, { useState } from "react";
import { Box, Paper, Typography, TextField, MenuItem, Alert, Grid, Button } from "@mui/material";
import { Spotify } from "react-spotify-embed";
import { InstagramEmbed } from "react-social-media-embed";

const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const UserContentBox = ({ currentUserProfile }) => {
  const [type, setType] = useState("spotify");
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setLink("");
    setTitle("");
    setError("");
    setSuccess("");
  };

  const handleLinkChange = (e) => {
    const value = e.target.value;
    setLink(value);
    setError("");
    setSuccess("");
    if (type === "spotify" && value && !/^https:\/\/open\.spotify\.com\//.test(value)) {
      setError("Please enter a valid Spotify link.");
    } else if (type === "instagram" && value && !/^https:\/\/(www\.)?instagram\.com\//.test(value)) {
      setError("Please enter a valid Instagram post link.");
    } else if (type === "link" && value && !/^https?:\/\//.test(value)) {
      setError("Please enter a valid URL starting with http:// or https://");
    } else {
      setError("");
    }
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (!title.trim()) {
        setError("Title is required.");
        setSaving(false);
        return;
      }
      if (!link.trim()) {
        setError("Link is required.");
        setSaving(false);
        return;
      }
      
      let url = "";
      let body = {};
      if (type === "spotify") {
        url = `${BACKEND_HOST}/playlists`;
        body = {data: { title, link }};
      } else if (type === "instagram") {
        url = `${BACKEND_HOST}/user-instagram-posts`;
        body = { title, link: link };
      } else {
        url = `${BACKEND_HOST}/links`;
        body = { data: { title, url: link}};
      }
      const token = window.localStorage.getItem("authToken");
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error("Failed to save. Please try again.");
      if (type === "spotify") {
        setSuccess("Playlist saved!");
      } else if (type === "instagram") {
        setSuccess("Instagram post saved!");
      } else {
        setSuccess("Link saved!");
      }
      setTitle("");
      setLink("");
    } catch (err) {
        console.debug(err)
      setError("Failed to save. Please try again.");
    }
    setSaving(false);
  };

  if (!currentUserProfile) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info">
          You must create a public profile before posting content. Visit your account page to add a public profile.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
            Share Spotify playlists, Instagram posts, or useful links with the community.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              label="Content Type"
              value={type}
              onChange={handleTypeChange}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="spotify">Spotify Playlist</MenuItem>
              <MenuItem value="instagram">Instagram Post</MenuItem>
              <MenuItem value="link">Regular Link</MenuItem>
            </TextField>
            <TextField
              label="Title"
              value={title}
              onChange={handleTitleChange}
              fullWidth
            />
            <TextField
              label={
                type === "spotify" ? "Spotify Link" : 
                type === "instagram" ? "Instagram Link" : 
                "Website URL"
              }
              value={link}
              onChange={handleLinkChange}
              fullWidth
            />
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
            {success && (
              <Alert severity="success">{success}</Alert>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={
                saving ||
                !title.trim() ||
                !link.trim() ||
                !!error
              }
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {link && !error && (
            <Box>
              {type === "spotify" ? (
                <Spotify link={link} wide />
              ) : type === "instagram" ? (
                <InstagramEmbed url={link} width={328} />
              ) : (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {title || "Link Preview"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {link}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    href={link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    size="small"
                  >
                    Visit Link
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserContentBox;